/**
 * 순찰 QR 관리 — 2026-09-23 시제품 → 2026-09-24 순찰 계획 추가(대표 지시)
 * - 2026-10-01: 순찰 회차는 총무 업무의 순찰 칸(🚶 QR 순찰)에서 온다 — QR 전용 시간표는 없앴다(대표 결정).
 *   회차마다 모든 구역 QR 을 제때 찍었는지 정상·늦음·누락으로 보고, 다 찍으면 총무 업무 칸이 자동 체크된다.
 * - 날짜별 기록을 계속 넘겨 볼 수 있다(최근 7일씩).
 * - 2026-09-24 대표 결정: 매장 공용 '순찰 태블릿' 을 순찰 기기로 등록하고, 순찰자는 이름 + 4자리 번호로 확인.
 *   QR 을 찍으면 그 구역 좌석 점검표가 뜬다. 원장 아이디는 태블릿에 로그인해 두지 않는다.
 */
import React, { useCallback, useEffect, useState } from 'react';
import QRCode from 'react-qr-code';
import { useNavigate } from 'react-router-dom';
import { ownerLayoutService } from '../services/OwnerLayoutService';
import { API_BASE_URL } from '../../../config/api';
import { authedFetch } from '../../../infrastructure/api/authedFetch';
import { PATROL_DEVICE_KEY } from '../../../pages/PatrolScanPage';
import { SeatPicker, RoomInfo, SeatInfo } from './shared/ZoneSeatPicker';

interface Zone {
  id: string;
  name: string;
  seat_numbers: number[];
  qr_token: string;
  is_active: boolean;
  today_scan_count: number;
  last_scanned_at: string | null;
  last_scanned_by: string | null;
  layout_zone_id?: number | null;   // 배치도 구역 (좌석은 거기서 읽는다)
}
/** 배치도에서 만든 구역 중 아직 순찰 QR 이 없는 것 */
interface LayoutZoneFree { id: number; name: string; color?: string; room_name?: string | null; seat_numbers: number[] }
interface Scan {
  id: string;
  zone_name: string;
  staff_name: string | null;
  scanned_at: string;
}

/** "1-30, 45" → [1..30, 45] */
function parseSeatInput(text: string): number[] {
  const out = new Set<number>();
  for (const part of text.split(',')) {
    const p = part.trim();
    if (!p) continue;
    const m = p.match(/^(\d+)\s*[-~]\s*(\d+)$/);
    if (m) {
      const a = parseInt(m[1], 10);
      const b = parseInt(m[2], 10);
      for (let i = Math.min(a, b); i <= Math.max(a, b) && out.size < 2000; i++) out.add(i);
    } else if (/^\d+$/.test(p)) {
      out.add(parseInt(p, 10));
    }
  }
  return Array.from(out).sort((x, y) => x - y);
}

function fmtTimeKST(iso: string | null): string {
  if (!iso) return '-';
  return new Date(iso).toLocaleTimeString('ko-KR', { timeZone: 'Asia/Seoul', hour: '2-digit', minute: '2-digit' });
}

/** 오늘(논리적 하루, 새벽 2시 경계) YYYY-MM-DD — 서버 순찰 기록과 같은 기준 */
const todayStr = () => {
  const d = new Date(Date.now() - 2 * 3600 * 1000);
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Seoul' }).format(d);
};
const shiftDay = (ymd: string, n: number) => {
  const d = new Date(`${ymd}T12:00:00+09:00`);
  d.setDate(d.getDate() + n);
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Seoul' }).format(d);
};
const DOW = ['월', '화', '수', '목', '금', '토', '일'];
const dowOf = (ymd: string) => DOW[(new Date(`${ymd}T12:00:00+09:00`).getDay() + 6) % 7];

/**
 * QR 에 넣을 주소. 원장이 매장 내부 주소(192.168.x)나 localhost 로 접속해 인쇄하면
 * 폰에서 열리지 않는 QR 이 된다(2026-09-24 검토) → 공개 주소일 때만 지금 주소를 쓴다.
 */
const publicOrigin = () => {
  const h = window.location.hostname;
  return h.endsWith('newattend.com') ? window.location.origin : 'https://app.newattend.com';
};

/** 순찰 회차 — 총무 업무의 순찰 칸에서 온다 (2026-10-01: QR 전용 시간표 폐지) */
interface PlanRound { time: string; end_time: string; label: string; task_count: number }
interface Cell {
  status: 'done' | 'late' | 'missed' | 'in_progress' | 'upcoming'; scanned_at: string | null; staff_name: string | null;
  checked?: boolean | null; finding_count?: number | null; quick?: boolean; note?: string | null;
}
interface Round { time: string; end_time?: string; label?: string; start: string; cells: Record<string, Cell>; duration_minutes?: number | null }
interface Finding {
  at: string; zone_name: string; seat_number: number | null; student_name: string | null;
  kind: 'not_at_seat' | 'item'; item_name: string | null; recorded_state: string | null; staff_name: string | null;
}
interface Device { id: string; name: string; created_at: string | null; last_used_at: string | null }
interface PatrolPerson { id: string; name: string; role: string; staff_type: string | null; has_pin: boolean; locked: boolean }
interface Summary { planned: number; done: number; late: number; missed: number; pending: number }
interface Board {
  date: string; planned_today: boolean; round_count?: number;
  zones: { id: string; name: string }[]; rounds: Round[];
  extra_scans: { zone_name: string; scanned_at: string; staff_name: string | null }[];
  summary: Summary; scans: Scan[]; findings?: Finding[];
}
interface DayHistory { date: string; planned_today: boolean; summary: Summary; rounds: Round[]; zones: { id: string; name: string }[]; scans: Scan[] }

const CELL: Record<Cell['status'], { bg: string; fg: string; label: string }> = {
  done: { bg: '#dcfce7', fg: '#166534', label: '정상' },
  late: { bg: '#fef3c7', fg: '#92400e', label: '늦음' },
  missed: { bg: '#fee2e2', fg: '#991b1b', label: '누락' },
  in_progress: { bg: '#dbeafe', fg: '#1e40af', label: '지금 순찰할 시간' },
  upcoming: { bg: '#f3f4f6', fg: '#6b7280', label: '예정' },
};

function SummaryLine({ s }: { s: Summary }) {
  const tag = (bg: string, fg: string, t: string) => (
    <span style={{ background: bg, color: fg, borderRadius: 6, padding: '2px 8px', fontSize: 12, fontWeight: 600 }}>{t}</span>
  );
  return (
    <span style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap' }}>
      {tag('#dcfce7', '#166534', `정상 ${s.done}`)}
      {s.late > 0 && tag('#fef3c7', '#92400e', `늦음 ${s.late}`)}
      {s.missed > 0 && tag('#fee2e2', '#991b1b', `누락 ${s.missed}`)}
      {s.pending > 0 && tag('#f3f4f6', '#6b7280', `남음 ${s.pending}`)}
    </span>
  );
}

/** 회차(행) × 구역(열) 순찰표 */
function BoardTable({ zones, rounds }: { zones: { id: string; name: string }[]; rounds: Round[] }) {
  if (rounds.length === 0) return null;
  const th: React.CSSProperties = { padding: '6px 8px', fontSize: 13, color: '#6b7280', textAlign: 'left', whiteSpace: 'nowrap' };
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ borderCollapse: 'separate', borderSpacing: 4, fontSize: 13 }}>
        <thead>
          <tr><th style={th}>순찰 시각</th>{zones.map((z) => <th key={z.id} style={th}>{z.name}</th>)}</tr>
        </thead>
        <tbody>
          {rounds.map((r) => (
            <tr key={r.start}>
              <td style={{ padding: '6px 8px', fontWeight: 700 }}>
                {r.time}{r.end_time && r.end_time !== r.time ? `~${r.end_time}` : ''}
                {r.label && <div style={{ fontSize: 11, fontWeight: 600, color: '#2563eb' }}>{r.label}</div>}
                {r.duration_minutes != null && <div style={{ fontSize: 11, fontWeight: 500, color: '#6b7280' }}>한 바퀴 {r.duration_minutes}분</div>}
              </td>
              {zones.map((z) => {
                const c = r.cells[z.id];
                if (!c) return <td key={z.id} title="구역을 만들기 전 회차" style={{ color: '#d1d5db', textAlign: 'center', padding: '6px 10px' }}>—</td>;
                const st = CELL[c.status];
                return (
                  <td key={z.id} style={{ background: st.bg, color: st.fg, borderRadius: 8, padding: '6px 10px', minWidth: 96 }}>
                    <div style={{ fontWeight: 700 }}>{st.label}</div>
                    {c.scanned_at && <div style={{ fontSize: 12 }}>{fmtTimeKST(c.scanned_at)}{c.staff_name ? ` · ${c.staff_name}` : ''}</div>}
                    {c.scanned_at && (
                      <div style={{ fontSize: 11, marginTop: 2 }}>
                        {c.checked === true && <span title="좌석 점검까지 마침">✓점검{c.finding_count ? ` · 이상 ${c.finding_count}` : ''}</span>}
                        {c.checked === false && <span title="QR 만 찍고 좌석 점검을 마치지 않음" style={{ color: '#b45309' }}>점검 안 함</span>}
                        {c.quick && <span title="다른 구역을 몇 초 만에 연달아 찍음 — 실제로 돌았는지 확인해 보세요" style={{ color: '#b91c1c', marginLeft: 4 }}>⚡빠름</span>}
                      </div>
                    )}
                    {c.note && <div style={{ fontSize: 11, marginTop: 2, color: '#374151', maxWidth: 160 }} title={c.note}>📝 {c.note}</div>}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** 순찰 점검에서 표시된 것 — 매장 '순찰 항목' 기준 한 목록 (2026-10-01: QR 전용 '자리에 없음' 폐지) */
function FindingsBlock({ findings }: { findings: Finding[] }) {
  const items = findings.filter((f) => f.kind === 'item');
  const row = (f: Finding, i: number) => (
    <div key={i} style={{ fontSize: 13, padding: '3px 0' }}>
      {fmtTimeKST(f.at)} · {f.zone_name} · {f.seat_number}번 <b>{f.student_name}</b>
      {f.kind === 'item' ? ` · ${f.item_name}` : ''}{f.staff_name ? ` · 확인 ${f.staff_name}` : ''}
    </div>
  );
  return (
    <div style={{ marginTop: 12 }}>
      {items.length > 0 && (
        <div style={{ background: '#f9fafb', borderRadius: 8, padding: 10 }}>
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>순찰에서 표시한 것 {items.length}건</div>
          {items.map(row)}
        </div>
      )}
    </div>
  );
}
/** 누르면 한 번 더 확인하는 버튼 — window.confirm 은 매장 PC 에서 막혀 '반응 없음'으로 보인다 */
function ConfirmButton({ label, confirmLabel, onConfirm, danger, style }: {
  label: string; confirmLabel: string; onConfirm: () => void; danger?: boolean; style: React.CSSProperties;
}) {
  const [asking, setAsking] = useState(false);
  if (!asking) {
    return <button style={{ ...style, ...(danger ? { color: '#ef4444', borderColor: '#fca5a5' } : {}) }} onClick={() => setAsking(true)}>{label}</button>;
  }
  return (
    <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
      <button style={{ ...style, background: danger ? '#ef4444' : '#2563eb', color: '#fff', border: 'none' }}
        onClick={() => { setAsking(false); onConfirm(); }}>{confirmLabel}</button>
      <button style={style} onClick={() => setAsking(false)}>취소</button>
    </span>
  );
}

export default function PatrolQrManagement() {
  const [zones, setZones] = useState<Zone[]>([]);
  const [layoutFree, setLayoutFree] = useState<LayoutZoneFree[]>([]);
  const [name, setName] = useState('');
  const [seatText, setSeatText] = useState('');           // 좌석 목록을 못 불러왔을 때만 쓰는 예비 입력
  const [picked, setPicked] = useState<Set<number>>(new Set());
  const [rooms, setRooms] = useState<RoomInfo[]>([]);
  const [seats, setSeats] = useState<SeatInfo[]>([]);
  const [seatsFailed, setSeatsFailed] = useState(false);
  const [editZone, setEditZone] = useState<string | null>(null);
  const [editPicked, setEditPicked] = useState<Set<number>>(new Set());
  const [busy, setBusy] = useState(false);
  const [openQr, setOpenQr] = useState<string | null>(null);
  const [notice, setNotice] = useState<string>('');

  const navigate = useNavigate();
  // 순찰 회차(읽기 전용) — 시각은 총무 업무 편집에서 정한다
  const [planRounds, setPlanRounds] = useState<PlanRound[]>([]);
  // 순찰표·기록
  const [boardDate, setBoardDate] = useState<string>(todayStr());
  const [board, setBoard] = useState<Board | null>(null);
  const [history, setHistory] = useState<DayHistory[]>([]);
  const [histTo, setHistTo] = useState<string>(todayStr());
  const [openDay, setOpenDay] = useState<string | null>(null);
  // 순찰 기기 · 순찰자 번호 (2026-09-24)
  const [devices, setDevices] = useState<Device[]>([]);
  const [people, setPeople] = useState<PatrolPerson[]>([]);
  const [pinDraft, setPinDraft] = useState<Record<string, string>>({});
  const [thisIsDevice, setThisIsDevice] = useState<boolean>(!!localStorage.getItem(PATROL_DEVICE_KEY));

  const api = (path: string, init?: RequestInit) => authedFetch(`${API_BASE_URL}${path}`, init);
  const flash = (m: string) => { setNotice(m); window.setTimeout(() => setNotice(''), 3500); };

  const loadZones = useCallback(async () => {
    try {
      const r = await api('/api/v1/owner/patrol-qr/zones');
      if (r.ok) { const d = await r.json(); setZones(d.zones || []); setLayoutFree(d.layout_zones_without_qr || []); }
    } catch { /* noop */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const loadPlan = useCallback(async () => {
    try { const r = await api('/api/v1/owner/patrol-qr/plan'); if (r.ok) setPlanRounds((await r.json()).rounds || []); } catch { /* noop */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const loadBoard = useCallback(async (d: string) => {
    try { const r = await api(`/api/v1/owner/patrol-qr/board?date=${d}`); if (r.ok) setBoard(await r.json()); } catch { /* noop */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const loadHistory = useCallback(async (to: string) => {
    try {
      const r = await api(`/api/v1/owner/patrol-qr/history?date_to=${to}&date_from=${shiftDay(to, -6)}`);
      if (r.ok) setHistory((await r.json()).days || []);
    } catch { /* noop */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const loadSeats = useCallback(async () => {
    try {
      const [sr, rr] = await Promise.all([api('/api/v1/owner/seats-management'), api('/api/v1/owner/rooms')]);
      if (!sr.ok) throw new Error('seats');
      const sd = await sr.json();
      const list: SeatInfo[] = (sd.seats || [])
        .filter((x: any) => Number.isFinite(Number(x.seat_number)))
        .map((x: any) => ({ seat_number: Number(x.seat_number), room_id: x.room_id || '', is_out_of_order: x.is_out_of_order }));
      setSeats(list);
      if (rr.ok) { const rd = await rr.json(); setRooms(Array.isArray(rd) ? rd : (rd.rooms || [])); }
      setSeatsFailed(list.length === 0);
    } catch { setSeatsFailed(true); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadDevices = useCallback(async () => {
    try { const r = await api('/api/v1/owner/patrol-qr/devices'); if (r.ok) setDevices((await r.json()).devices || []); } catch { /* noop */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const loadPeople = useCallback(async () => {
    try { const r = await api('/api/v1/owner/patrol-qr/staff'); if (r.ok) setPeople((await r.json()).staff || []); } catch { /* noop */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { loadZones(); loadPlan(); loadSeats(); loadDevices(); loadPeople(); }, [loadZones, loadPlan, loadSeats, loadDevices, loadPeople]);
  useEffect(() => { loadBoard(boardDate); }, [loadBoard, boardDate]);
  useEffect(() => { loadHistory(histTo); }, [loadHistory, histTo]);
  // 오늘 순찰표는 1분마다 새로고침 — 총무가 찍는 대로 원장 화면에 반영
  useEffect(() => {
    if (boardDate !== todayStr()) return undefined;
    const t = window.setInterval(() => loadBoard(boardDate), 60_000);
    return () => window.clearInterval(t);
  }, [boardDate, loadBoard]);

  const refreshAll = async () => { await Promise.all([loadZones(), loadBoard(boardDate), loadHistory(histTo)]); };

  // ---------- 구역 ----------
  const takenMap = (exceptZoneId?: string) => {
    const m = new Map<number, string>();
    zones.forEach((z) => { if (z.id !== exceptZoneId) z.seat_numbers.forEach((n) => { if (!m.has(n)) m.set(n, z.name); }); });
    layoutFree.forEach((z) => z.seat_numbers.forEach((n) => { if (!m.has(n)) m.set(n, z.name); }));
    return m;
  };
  // 배치도에 이미 있는 구역에 QR 만 붙이기 (2026-10-04)
  const attachQr = async (lz: LayoutZoneFree) => {
    setBusy(true);
    try {
      const r = await api('/api/v1/owner/patrol-qr/zones', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ layout_zone_id: lz.id }),
      });
      if (r.ok) { flash(`'${lz.name}' 구역에 QR 을 만들었습니다. [인쇄]로 뽑아 붙여 주세요`); await refreshAll(); }
      else { const d = await r.json().catch(() => ({})); alert(typeof d?.detail === 'string' ? d.detail : 'QR 을 만들지 못했습니다'); }
    } finally { setBusy(false); }
  };
  const createZone = async () => {
    const chosen = seatsFailed ? parseSeatInput(seatText) : Array.from(picked).sort((a, b) => a - b);
    if (!name.trim()) { alert('구역 이름을 입력하세요 (예: 2층 A구역)'); return; }
    if (chosen.length === 0) { alert('이 구역에 들어갈 좌석을 골라 주세요'); return; }
    setBusy(true);
    try {
      const r = await api('/api/v1/owner/patrol-qr/zones', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim(), seat_numbers: chosen }),
      });
      if (r.ok) { setName(''); setSeatText(''); setPicked(new Set()); flash('구역을 만들었습니다. [인쇄]로 QR 을 뽑아 붙여 주세요'); await refreshAll(); }
      else alert('구역 등록에 실패했습니다');
    } finally { setBusy(false); }
  };
  const saveZoneSeats = async (z: Zone) => {
    setBusy(true);
    try {
      const r = await api(`/api/v1/owner/patrol-qr/zones/${z.id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ seat_numbers: Array.from(editPicked).sort((a, b) => a - b) }),
      });
      if (r.ok) { setEditZone(null); flash('좌석을 저장했습니다'); await loadZones(); } else alert('좌석 저장에 실패했습니다');
    } finally { setBusy(false); }
  };
  const deleteZone = async (z: Zone) => {
    setBusy(true);
    try { const r = await api(`/api/v1/owner/patrol-qr/zones/${z.id}`, { method: 'DELETE' }); if (r.ok) { flash(`'${z.name}' 구역을 삭제했습니다`); await refreshAll(); } }
    finally { setBusy(false); }
  };
  const regenerate = async (z: Zone) => {
    setBusy(true);
    try {
      const r = await api(`/api/v1/owner/patrol-qr/zones/${z.id}/regenerate`, { method: 'POST' });
      if (r.ok) { await loadZones(); flash('새 QR 이 발급되었습니다. 새로 인쇄해서 붙여 주세요'); }
    } finally { setBusy(false); }
  };
  // ---------- 순찰 기기 · 순찰자 번호 ----------
  const registerThisDevice = async () => {
    setBusy(true);
    try {
      const r = await api('/api/v1/owner/patrol-qr/devices', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name: '순찰 태블릿' }),
      });
      if (!r.ok) { alert('등록에 실패했습니다'); return; }
      const d = await r.json();
      localStorage.setItem(PATROL_DEVICE_KEY, d.device_token);
      setThisIsDevice(true);
      flash('이 기기를 순찰 기기로 등록했습니다. 이제 이 기기에서 원장 계정은 로그아웃해 두세요');
      await loadDevices();
    } finally { setBusy(false); }
  };
  const revokeDevice = async (d: Device) => {
    setBusy(true);
    try {
      const r = await api(`/api/v1/owner/patrol-qr/devices/${d.id}`, { method: 'DELETE' });
      if (r.ok) { flash(`'${d.name}' 등록을 해제했습니다. 그 기기로는 더 이상 기록되지 않습니다`); await loadDevices(); }
    } finally { setBusy(false); }
  };
  const forgetThisDevice = () => { localStorage.removeItem(PATROL_DEVICE_KEY); setThisIsDevice(false); };
  const savePin = async (p: PatrolPerson) => {
    const pin = (pinDraft[p.id] || '').trim();
    if (!/^\d{4}$/.test(pin)) { alert('숫자 4자리로 정해 주세요'); return; }
    setBusy(true);
    try {
      const r = await api(`/api/v1/owner/patrol-qr/staff/${p.id}/pin`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ pin }),
      });
      if (r.ok) { setPinDraft({ ...pinDraft, [p.id]: '' }); flash(`${p.name} 님 번호를 저장했습니다`); await loadPeople(); }
      else { const d = await r.json().catch(() => ({})); alert(typeof d.detail === 'string' ? d.detail : '저장에 실패했습니다'); }
    } finally { setBusy(false); }
  };
  const clearPin = async (p: PatrolPerson) => {
    setBusy(true);
    try { const r = await api(`/api/v1/owner/patrol-qr/staff/${p.id}/pin`, { method: 'DELETE' }); if (r.ok) { flash(`${p.name} 님 번호를 지웠습니다`); await loadPeople(); } }
    finally { setBusy(false); }
  };

  const scanUrl = (z: Zone) => `${publicOrigin()}/patrol-scan/${z.qr_token}`;
  const printQr = (z: Zone) => {
    const svg = document.getElementById(`patrol-qr-svg-${z.id}`);
    if (!svg) { alert('먼저 [QR 보기]를 눌러 QR 을 연 뒤 인쇄하세요.'); return; }
    const w = window.open('', '_blank', 'width=480,height=640');
    if (!w) { alert('팝업이 차단되어 인쇄 창을 열 수 없습니다.'); return; }
    w.document.write(`
      <html><head><title>순찰 QR - ${z.name}</title></head>
      <body style="text-align:center;font-family:sans-serif;padding:40px;">
        <h1 style="margin-bottom:8px;">${z.name}</h1>
        <p style="color:#555;margin-top:0;">순찰할 때 순찰 태블릿으로 이 QR 을 찍어 주세요</p>
        <div style="margin:24px auto;width:280px;">${svg.outerHTML.replace(/width="\d+"/, 'width="280"').replace(/height="\d+"/, 'height="280"')}</div>
        <p style="color:#888;font-size:13px;">NewAttend 순찰 확인용 · 개인 휴대폰으로 찍으면 기록되지 않습니다</p>
        <script>window.onload = function(){ window.print(); }</` + `script>
      </body></html>`);
    w.document.close();
  };

  const box: React.CSSProperties = { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 20, marginBottom: 16 };
  const btn: React.CSSProperties = { padding: '6px 12px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer', fontSize: 13 };
  const primary: React.CSSProperties = { ...btn, background: '#2563eb', color: '#fff', border: 'none', fontWeight: 600, padding: '8px 16px' };
  const isToday = boardDate === todayStr();

  return (
    <div style={{ padding: 24, maxWidth: 1000 }}>
      <h2 style={{ fontSize: 22, fontWeight: 700, marginTop: 0 }}>🚶 순찰 QR</h2>
      <p style={{ color: '#6b7280', fontSize: 14, marginTop: 4 }}>
        순찰 시각은 <b>총무 업무의 순찰 칸</b>입니다. 그 시간마다 구역 QR 을 제때 찍었는지 <b>정상 · 늦음 · 누락</b>으로 보여 드리고,
        모든 구역을 찍으면 총무 업무 순찰 칸이 자동으로 체크됩니다.
        순찰 태블릿으로 QR 을 찍으면 그 구역 좌석 점검표가 열리고, 총무님이 [벌점 관리] → 설정의 순찰 항목으로 표시한 것이 여기에 남습니다.
        학생 휴대폰으로 찍으면 기록되지 않습니다.
      </p>
      {notice && <div style={{ background: '#ecfdf5', color: '#065f46', padding: '8px 12px', borderRadius: 8, marginBottom: 12, fontSize: 14 }}>{notice}</div>}

      {/* ① 오늘 순찰표 */}
      <div style={box}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>{isToday ? '오늘 순찰표' : '순찰표'}</h3>
          <button style={btn} onClick={() => setBoardDate(shiftDay(boardDate, -1))}>◀</button>
          <span style={{ fontWeight: 600 }}>{boardDate} ({dowOf(boardDate)})</span>
          <button style={btn} disabled={isToday} onClick={() => setBoardDate(shiftDay(boardDate, 1))}>▶</button>
          {!isToday && <button style={btn} onClick={() => setBoardDate(todayStr())}>오늘</button>}
          {board && board.planned_today && <span style={{ marginLeft: 'auto' }}><SummaryLine s={board.summary} /></span>}
        </div>
        {!board ? <div style={{ color: '#9ca3af', padding: '12px 0' }}>불러오는 중…</div>
          : board.zones.length === 0 ? <div style={{ color: '#9ca3af', padding: '12px 0' }}>아직 구역이 없습니다. 아래 [구역 관리]에서 먼저 구역을 만들어 주세요.</div>
            : !board.planned_today ? (
              <div style={{ color: '#9ca3af', padding: '12px 0' }}>
                {planRounds.length === 0 ? '순찰 칸이 아직 없습니다. 아래 [순찰 시각]에서 총무 업무의 순찰 칸을 연결해 주세요.' : '이 날은 챙길 순찰 회차가 없습니다 (순찰 칸이 없는 요일이거나 연결 전 날짜).'}
              </div>
            ) : (
              <div style={{ marginTop: 12 }}><BoardTable zones={board.zones} rounds={board.rounds} /></div>
            )}
        {board && board.extra_scans.length > 0 && (
          <div style={{ marginTop: 10, fontSize: 13, color: '#6b7280' }}>
            계획 외 순찰 {board.extra_scans.length}건: {board.extra_scans.map((x) => `${fmtTimeKST(x.scanned_at)} ${x.zone_name}`).join(' · ')}
          </div>
        )}
        {board && (board.findings || []).length > 0 && <FindingsBlock findings={board.findings || []} />}
      </div>

      {/* ② 순찰 기록 (날짜별로 계속) */}
      <div style={box}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>순찰 기록</h3>
          <button style={btn} onClick={() => setHistTo(shiftDay(histTo, -7))}>◀ 이전 7일</button>
          <span style={{ fontSize: 14 }}>{shiftDay(histTo, -6)} ~ {histTo}</span>
          <button style={btn} disabled={histTo >= todayStr()} onClick={() => setHistTo(shiftDay(histTo, 7) > todayStr() ? todayStr() : shiftDay(histTo, 7))}>다음 7일 ▶</button>
        </div>
        <div style={{ marginTop: 10 }}>
          {history.map((d) => (
            <div key={d.date} style={{ borderTop: '1px solid #f3f4f6', padding: '10px 0' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', cursor: 'pointer' }}
                onClick={() => setOpenDay(openDay === d.date ? null : d.date)}>
                <span style={{ fontWeight: 700, minWidth: 130 }}>{d.date} ({dowOf(d.date)})</span>
                {d.planned_today ? <SummaryLine s={d.summary} /> : <span style={{ fontSize: 12, color: '#9ca3af' }}>챙길 회차 없음</span>}
                <span style={{ fontSize: 13, color: '#6b7280' }}>찍은 기록 {d.scans.length}건</span>
                <span style={{ marginLeft: 'auto', fontSize: 13, color: '#2563eb' }}>{openDay === d.date ? '접기 ▲' : '자세히 ▼'}</span>
              </div>
              {openDay === d.date && (
                <div style={{ marginTop: 10 }}>
                  {d.planned_today && <BoardTable zones={d.zones} rounds={d.rounds} />}
                  {d.scans.length === 0 ? <div style={{ color: '#9ca3af', fontSize: 13, marginTop: 8 }}>찍은 기록이 없습니다.</div> : (
                    <table style={{ width: '100%', marginTop: 10, fontSize: 13, borderCollapse: 'collapse' }}>
                      <thead><tr style={{ textAlign: 'left', color: '#6b7280' }}><th style={{ padding: 4 }}>시각</th><th style={{ padding: 4 }}>구역</th><th style={{ padding: 4 }}>찍은 계정</th></tr></thead>
                      <tbody>
                        {d.scans.map((x) => (
                          <tr key={x.id} style={{ borderTop: '1px solid #f3f4f6' }}>
                            <td style={{ padding: 4 }}>{fmtTimeKST(x.scanned_at)}</td><td style={{ padding: 4 }}>{x.zone_name}</td><td style={{ padding: 4 }}>{x.staff_name || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* 순찰 기기 · 순찰자 번호 (2026-09-24) */}
      <div style={box}>
        <h3 style={{ fontSize: 16, fontWeight: 700, marginTop: 0 }}>순찰 기기 · 순찰자 번호</h3>
        <div style={{ fontSize: 13, color: '#6b7280', lineHeight: 1.7 }}>
          ① 순찰 태블릿에서 이 화면을 열고 <b>[이 기기를 순찰 기기로 등록]</b>을 누르세요. ② 등록한 뒤에는 그 태블릿에서 원장 계정을 <b>로그아웃</b>해 두세요
          (로그아웃해도 순찰 기기 등록은 남습니다). ③ 순찰하는 분마다 아래에서 4자리 번호를 정해 주세요. 태블릿에서 이름을 고르고 번호를 넣으면 90분 동안 그 사람 이름으로 기록됩니다.
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 12 }}>
          {thisIsDevice
            ? <span style={{ background: '#dcfce7', color: '#166534', borderRadius: 8, padding: '6px 10px', fontSize: 13, fontWeight: 600 }}>✓ 지금 이 기기는 순찰 기기입니다</span>
            : <button style={primary} disabled={busy} onClick={registerThisDevice}>이 기기를 순찰 기기로 등록</button>}
          {thisIsDevice && <ConfirmButton style={btn} label="이 기기 표시만 지우기" confirmLabel="이 기기에서 순찰 기기 표시 지우기" onConfirm={forgetThisDevice} />}
        </div>
        <div style={{ marginTop: 10, fontSize: 14 }}>
          {devices.length === 0 ? <span style={{ color: '#9ca3af' }}>등록된 순찰 기기가 없습니다.</span> : devices.map((d) => (
            <div key={d.id} style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '6px 0', borderTop: '1px solid #f3f4f6' }}>
              <span style={{ fontWeight: 600 }}>{d.name}</span>
              <span style={{ fontSize: 12, color: '#6b7280' }}>
                등록 {d.created_at ? d.created_at.slice(0, 10) : '-'} · 마지막 사용 {d.last_used_at ? `${d.last_used_at.slice(5, 10)} ${fmtTimeKST(d.last_used_at)}` : '없음'}
              </span>
              <span style={{ marginLeft: 'auto' }}>
                <ConfirmButton style={btn} danger label="등록 해제" confirmLabel="해제 (그 기기로 기록 안 됨)" onConfirm={() => revokeDevice(d)} />
              </span>
            </div>
          ))}
        </div>
        <div style={{ marginTop: 14, fontWeight: 700, fontSize: 14 }}>순찰자 번호</div>
        <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 6 }}>
          [직원 관리]에 등록된 분이 여기 나옵니다. 번호를 5번 틀리면 잠기고, 번호를 다시 저장하면 풀립니다. 그만둔 분은 [직원 관리]에서 비활성화하면 바로 못 씁니다.
        </div>
        {people.length === 0 ? <div style={{ color: '#9ca3af', fontSize: 13 }}>등록된 직원이 없습니다. [직원 관리]에서 먼저 등록해 주세요.</div> : people.map((p) => (
          <div key={p.id} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '6px 0', borderTop: '1px solid #f3f4f6', flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 600, minWidth: 90 }}>{p.name}</span>
            <span style={{ fontSize: 12, color: '#6b7280', minWidth: 40 }}>{p.role === 'owner' ? '원장' : '직원'}</span>
            {p.locked
              ? <span style={{ fontSize: 12, color: '#b91c1c', fontWeight: 600 }}>🔒 잠김</span>
              : p.has_pin ? <span style={{ fontSize: 12, color: '#166534' }}>번호 있음</span> : <span style={{ fontSize: 12, color: '#9ca3af' }}>번호 없음</span>}
            <input value={pinDraft[p.id] || ''} inputMode="numeric" maxLength={4} placeholder="4자리"
              onChange={(e) => setPinDraft({ ...pinDraft, [p.id]: e.target.value.replace(/\D/g, '').slice(0, 4) })}
              style={{ width: 70, padding: '4px 8px', border: '1px solid #d1d5db', borderRadius: 6, marginLeft: 'auto' }} />
            <button style={btn} disabled={busy} onClick={() => savePin(p)}>{p.locked ? '새 번호로 풀기' : p.has_pin ? '번호 바꾸기' : '번호 저장'}</button>
            {p.has_pin && <ConfirmButton style={btn} danger label="지우기" confirmLabel="번호 지우기" onConfirm={() => clearPin(p)} />}
          </div>
        ))}
      </div>

      {/* ③ 순찰 시각 — 총무 업무 순찰 칸 (2026-10-01 대표 결정: 순찰 일정은 총무 업무 하나) */}
      <div style={box}>
        <h3 style={{ fontSize: 16, fontWeight: 700, marginTop: 0 }}>순찰 시각</h3>
        <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 8 }}>
          순찰 시각은 [총무 업무 편집]의 <b>🚶 QR 순찰</b> 칸입니다. 같은 시간대 칸이 한 회차이고, 시작 5분 전부터 끝 시각까지 찍으면 정상입니다.
          시각을 바꾸려면 총무 업무 편집에서 고쳐 주세요.
        </div>
        {planRounds.length === 0 ? (
          <div style={{ color: '#9ca3af', fontSize: 13 }}>오늘 연결된 순찰 칸이 없습니다.</div>
        ) : (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {planRounds.map((r) => (
              <span key={`${r.time}-${r.end_time}`} style={{ background: '#eff6ff', color: '#1d4ed8', borderRadius: 8, padding: '4px 10px', fontSize: 13, fontWeight: 600 }}>
                {r.time}~{r.end_time}{r.label ? ` · ${r.label}` : ''}
              </span>
            ))}
          </div>
        )}
        <div style={{ marginTop: 12 }}>
          <button style={primary} onClick={() => navigate(ownerLayoutService.getOwnerPath('/owner/staff-tasks'))}>총무 업무 편집으로</button>
        </div>
      </div>

      {/* ④ 구역 관리 */}
      <div style={box}>
        <h3 style={{ fontSize: 16, fontWeight: 700, marginTop: 0 }}>구역 관리</h3>
        <div style={{ fontSize: 13, color: '#6b7280', margin: '-4px 0 10px' }}>
          여기 구역은 <b>[시설 관리] → [통합 레이아웃 관리]</b>의 구역과 같습니다. 어느 쪽에서 만들거나 좌석을 바꿔도 양쪽에 똑같이 반영됩니다.
          좌석은 한 구역에만 들어갑니다.
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="구역 이름 (예: 2층 A구역)"
            style={{ flex: 1, minWidth: 180, padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 8 }} />
          {seatsFailed && (
            <input value={seatText} onChange={(e) => setSeatText(e.target.value)} placeholder="포함 좌석 번호 (예: 1-30, 45)"
              style={{ flex: 1, minWidth: 180, padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 8 }} />
          )}
          <span style={{ fontSize: 13, color: '#374151', fontWeight: 600 }}>
            {seatsFailed ? `인식된 좌석 ${parseSeatInput(seatText).length}개` : `선택한 좌석 ${picked.size}개`}
          </span>
          <button onClick={createZone} disabled={busy} style={primary}>구역 추가</button>
        </div>
        {seatsFailed
          ? <div style={{ fontSize: 13, color: '#b45309', marginTop: 6 }}>좌석 목록을 불러오지 못해 번호를 직접 입력하도록 바꿨습니다.</div>
          : <SeatPicker rooms={rooms} seats={seats} selected={picked} onChange={setPicked} takenBy={takenMap()} />}

        <div style={{ marginTop: 16 }}>
          {zones.length === 0 ? (
            <div style={{ color: '#9ca3af', padding: '8px 0' }}>아직 구역이 없습니다. 위에서 첫 구역을 만들어 보세요.</div>
          ) : zones.map((z) => (
            <div key={z.id} style={{ borderTop: '1px solid #f3f4f6', padding: '12px 0' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <div style={{ fontWeight: 700, fontSize: 15 }}>{z.name}</div>
                <div style={{ fontSize: 13, color: '#6b7280' }}>
                  좌석 {z.seat_numbers.length}개{z.seat_numbers.length > 0 && ` (${z.seat_numbers.slice(0, 8).join(', ')}${z.seat_numbers.length > 8 ? ' …' : ''})`}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                <button style={btn} onClick={() => setOpenQr(openQr === z.id ? null : z.id)}>{openQr === z.id ? 'QR 닫기' : 'QR 보기'}</button>
                <button style={btn} onClick={() => printQr(z)}>인쇄</button>
                {!seatsFailed && (
                  <button style={btn} onClick={() => {
                    if (editZone === z.id) { setEditZone(null); return; }
                    setEditZone(z.id); setEditPicked(new Set(z.seat_numbers));
                  }}>{editZone === z.id ? '좌석 수정 닫기' : '좌석 수정'}</button>
                )}
                <ConfirmButton style={btn} label="QR 재발급" confirmLabel="지금 QR 무효로 하고 재발급" onConfirm={() => regenerate(z)} />
                <ConfirmButton style={btn} danger label="QR 삭제" confirmLabel="QR 삭제 (지난 기록·배치도 구역은 남음)" onConfirm={() => deleteZone(z)} />
              </div>
              {editZone === z.id && (
                <div style={{ marginTop: 12, padding: 12, background: '#f9fafb', borderRadius: 8 }}>
                  <SeatPicker rooms={rooms} seats={seats} selected={editPicked} onChange={setEditPicked} takenBy={takenMap(z.id)} />
                  <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center' }}>
                    <span style={{ fontSize: 13, fontWeight: 600 }}>선택한 좌석 {editPicked.size}개</span>
                    <button style={{ ...btn, background: '#2563eb', color: '#fff', border: 'none', fontWeight: 600 }}
                      disabled={busy || editPicked.size === 0} onClick={() => saveZoneSeats(z)}>좌석 저장</button>
                    <span style={{ fontSize: 12, color: '#6b7280' }}>좌석만 바뀌고 벽에 붙인 QR 은 그대로 쓸 수 있습니다. 배치도 구역에도 같이 반영됩니다.</span>
                  </div>
                </div>
              )}
              {openQr === z.id && (
                <div style={{ marginTop: 12, display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
                  <div style={{ background: '#fff', padding: 12, border: '1px solid #e5e7eb', borderRadius: 8 }}>
                    <QRCode id={`patrol-qr-svg-${z.id}`} value={scanUrl(z)} size={160} />
                  </div>
                  <div style={{ fontSize: 13, color: '#6b7280', wordBreak: 'break-all', maxWidth: 420 }}>
                    주소: {scanUrl(z)}<br /><br />[인쇄] 를 눌러 종이로 뽑아 구역 벽에 붙여 주세요.
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {layoutFree.length > 0 && (
          <div style={{ marginTop: 16, padding: 12, background: '#f9fafb', borderRadius: 8 }}>
            <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>배치도에만 있고 QR 이 없는 구역</div>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>[QR 만들기]를 누르면 그 구역 좌석 그대로 순찰 QR 이 생깁니다.</div>
            {layoutFree.map((lz) => (
              <div key={lz.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', flexWrap: 'wrap' }}>
                <span style={{ width: 10, height: 10, borderRadius: 5, background: lz.color || '#3B82F6', display: 'inline-block' }} />
                <span style={{ fontWeight: 600 }}>{lz.name}</span>
                {lz.room_name && <span style={{ fontSize: 12, color: '#6b7280' }}>{lz.room_name}</span>}
                <span style={{ fontSize: 13, color: '#6b7280' }}>좌석 {lz.seat_numbers.length}개</span>
                <button style={btn} disabled={busy || lz.seat_numbers.length === 0}
                  title={lz.seat_numbers.length === 0 ? '좌석이 없는 구역입니다. 통합 레이아웃 관리에서 좌석을 먼저 넣어 주세요' : undefined}
                  onClick={() => attachQr(lz)}>QR 만들기</button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

